/**
 * Confirmação de pré-voo da criação assistida por IA.
 *
 * Como a apreciação é gravada direto (sem tela de revisão), este modal é a
 * única oportunidade de corrigir a IDENTIDADE da máquina antes de gerar o
 * documento. Só ~21% das pastas casam com o cadastro por nome, e um casamento
 * difuso errado troca a máquina inteira do documento — por isso os campos de
 * baixa confiança aparecem em destaque e com as alternativas à mão.
 */

import { useEffect, useMemo, useState } from 'react';
import { Sparkles, AlertTriangle, Loader2, X } from 'lucide-react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import { escapeHtml } from '@/utils/html-escape';
import type {
  Entrevistado,
  OverrideContextoIA,
  PreviewIA,
} from '@/types/apreciacao-ia';
import { ConfiancaBadge } from './ConfiancaBadge';

interface Props {
  aberto: boolean;
  projetoId: string;
  machineFolder: string;
  machineName: string;
  /** Pasta já tem apreciação vinculada: exige confirmação extra. */
  jaTemApreciacao: boolean;
  onFechar: () => void;
  onIniciado: (jobId: string) => void;
}

function Linha({
  rotulo,
  children,
  destaque,
}: {
  rotulo: string;
  children: React.ReactNode;
  destaque?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-1 border-b border-gray-100 py-2 last:border-0 sm:flex-row sm:items-center sm:gap-3 ${
        destaque ? '-mx-2 rounded bg-amber-50 px-2' : ''
      }`}
    >
      <span className="w-44 shrink-0 text-xs font-medium uppercase tracking-wide text-gray-500">
        {rotulo}
      </span>
      <div className="min-w-0 flex-1 text-sm text-gray-900">{children}</div>
    </div>
  );
}

export function ConfirmarGeracaoIAModal({
  aberto,
  projetoId,
  machineFolder,
  machineName,
  jaTemApreciacao,
  onFechar,
  onIniciado,
}: Props) {
  const { t } = useI18n();
  const [preview, setPreview] = useState<PreviewIA | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [iniciando, setIniciando] = useState(false);

  // Correções do usuário
  const [ativoEscolhido, setAtivoEscolhido] = useState<string | null>(null);
  const [entrevistadoEscolhido, setEntrevistadoEscolhido] = useState<Entrevistado | null>(null);
  const [usarTemplate, setUsarTemplate] = useState(true);

  useEffect(() => {
    if (!aberto) return;

    let cancelado = false;
    setCarregando(true);
    setErro(null);
    setPreview(null);
    setAtivoEscolhido(null);
    setEntrevistadoEscolhido(null);
    setUsarTemplate(true);

    apiService
      .previewApreciacaoIA(projetoId, machineFolder)
      .then((r) => {
        if (cancelado) return;
        if (!r.success || !r.data) {
          setErro(r.message || t('apreciacaoIa.confirmar.erro.resolverContexto'));
          return;
        }
        setPreview(r.data);
      })
      .catch((e) => !cancelado && setErro(e instanceof Error ? e.message : t('apreciacaoIa.confirmar.erro.carregarContexto')))
      .finally(() => !cancelado && setCarregando(false));

    return () => {
      cancelado = true;
    };
  }, [aberto, projetoId, machineFolder, t]);

  const alternativasAtivo = preview?.ativoId.alternativas ?? [];

  const entrevistadoAtual = useMemo<Entrevistado | null>(
    () => entrevistadoEscolhido ?? preview?.entrevistado.valor ?? null,
    [entrevistadoEscolhido, preview]
  );

  async function iniciar() {
    if (!preview) return;
    setIniciando(true);
    setErro(null);

    const sobrescrever: OverrideContextoIA = {};
    if (ativoEscolhido) sobrescrever.ativoId = ativoEscolhido;
    if (entrevistadoEscolhido) sobrescrever.entrevistado = entrevistadoEscolhido;

    try {
      const r = await apiService.criarApreciacaoIA(projetoId, machineFolder, {
        forcar: true,
        templateMultiParteId: usarTemplate ? undefined : null,
        sobrescreverContexto: Object.keys(sobrescrever).length ? sobrescrever : undefined,
      });

      if (!r.success || !r.data?.jobId) {
        setErro(r.message || t('apreciacaoIa.confirmar.erro.iniciar'));
        return;
      }
      onIniciado(r.data.jobId);
    } catch (e) {
      setErro(e instanceof Error ? e.message : t('apreciacaoIa.confirmar.erro.iniciarInesperado'));
    } finally {
      setIniciando(false);
    }
  }

  if (!aberto) return null;

  const semFotos = preview?.totalFotos === 0;
  const poucasFotos = !!preview && preview.totalFotos > 0 && preview.totalFotos < 3;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">{t('apreciacaoIa.confirmar.titulo')}</h2>
              <p className="text-xs text-gray-500">{machineName}</p>
            </div>
          </div>
          <button
            onClick={onFechar}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label={t('comum.acao.fechar')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {carregando && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('apreciacaoIa.confirmar.carregando')}
            </div>
          )}

          {erro && (
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {erro}
            </div>
          )}

          {preview && (
            <>
              <p
                className="mb-3 text-sm text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: t('apreciacaoIa.confirmar.intro', {
                    fotos: preview.totalFotos,
                    cache:
                      preview.descricoesEmCache > 0
                        ? t('apreciacaoIa.confirmar.introCache', {
                            quantidade: preview.descricoesEmCache,
                          })
                        : '',
                  }),
                }}
              />

              <div className="rounded border border-gray-200 px-3 py-1">
                <Linha
                  rotulo={t('apreciacaoIa.confirmar.rotulo.maquina')}
                  destaque={preview.nomeMaquina.confianca !== 'alta'}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{preview.nomeMaquina.valor ?? '—'}</span>
                    <ConfiancaBadge
                      confianca={preview.nomeMaquina.confianca}
                      origem={preview.nomeMaquina.origem}
                    />
                  </div>
                  {preview.nomeMaquina.detalhe && (
                    <p className="mt-0.5 text-xs text-gray-500">{preview.nomeMaquina.detalhe}</p>
                  )}

                  {alternativasAtivo.length > 0 && (
                    <div className="mt-2">
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        {t('apreciacaoIa.confirmar.maquina.confirmarCadastro')}
                      </label>
                      <select
                        value={ativoEscolhido ?? preview.ativoId.valor ?? ''}
                        onChange={(e) => setAtivoEscolhido(e.target.value || null)}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      >
                        <option value="">{t('apreciacaoIa.confirmar.maquina.nenhuma')}</option>
                        {alternativasAtivo.map((a) => (
                          <option key={a.valor} value={a.valor}>
                            {a.rotulo}
                            {a.score !== undefined
                              ? t('apreciacaoIa.confirmar.maquina.semelhanca', { score: a.score })
                              : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </Linha>

                <Linha rotulo={t('comum.rotulo.tipo')}>
                  {preview.tipoMaquina.valor ?? (
                    <span className="text-gray-400">{t('apreciacaoIa.confirmar.tipo.naoInformado')}</span>
                  )}
                </Linha>

                <Linha
                  rotulo={t('apreciacaoIa.confirmar.rotulo.tagSismana')}
                  destaque={!preview.identificacao.valor}
                >
                  {preview.identificacao.valor ?? (
                    <span className="text-gray-400">
                      {t('apreciacaoIa.confirmar.identificacao.vazio')}
                    </span>
                  )}
                </Linha>

                <Linha rotulo={t('comum.rotulo.area')}>
                  {preview.areaExibicao.valor ?? <span className="text-gray-400">—</span>}
                </Linha>

                <Linha rotulo={t('apreciacaoIa.confirmar.rotulo.local')}>
                  {preview.local.valor ?? <span className="text-gray-400">—</span>}
                </Linha>

                <Linha
                  rotulo={t('apreciacaoIa.confirmar.rotulo.responsavel')}
                  destaque={preview.entrevistado.confianca !== 'alta'}
                >
                  {preview.entrevistadoAlternativas.length > 0 ? (
                    <select
                      value={entrevistadoAtual ? `${entrevistadoAtual.nome}|${entrevistadoAtual.cargo}` : ''}
                      onChange={(e) => {
                        const [nome, cargo] = e.target.value.split('|');
                        setEntrevistadoEscolhido(nome ? { nome, cargo: cargo ?? '' } : null);
                      }}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    >
                      <option value="">{t('apreciacaoIa.confirmar.entrevistado.deixarEmBranco')}</option>
                      {preview.entrevistadoAlternativas.map((a) => (
                        <option key={`${a.nome}|${a.cargo}`} value={`${a.nome}|${a.cargo}`}>
                          {t('apreciacaoIa.confirmar.entrevistado.opcao', {
                            nome: a.nome,
                            cargo: a.cargo,
                            ocorrencias: a.ocorrencias,
                            escopo: t(`apreciacaoIa.confirmar.entrevistado.escopo.${a.escopo}`),
                          })}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-gray-400">
                      {t('apreciacaoIa.confirmar.entrevistado.semHistorico')}
                    </span>
                  )}
                  {preview.entrevistado.detalhe && (
                    <p className="mt-0.5 text-xs text-gray-500">{preview.entrevistado.detalhe}</p>
                  )}
                </Linha>

                <Linha rotulo={t('apreciacaoIa.confirmar.rotulo.partes')}>
                  {preview.templateSugerido.valor ? (
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={usarTemplate}
                        onChange={(e) => setUsarTemplate(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300"
                      />
                      <span>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t('apreciacaoIa.confirmar.partes.usarModelo', {
                              nome: escapeHtml(preview.templateSugerido.valor.nome),
                              total: preview.templateSugerido.valor.partes.length,
                            }),
                          }}
                        />
                        <span className="block text-xs text-gray-500">
                          {t('apreciacaoIa.confirmar.partes.desmarcar')}
                        </span>
                      </span>
                    </label>
                  ) : (
                    <span className="text-gray-600">
                      {t('apreciacaoIa.confirmar.partes.semModelo')}
                    </span>
                  )}
                </Linha>
              </div>

              {(preview.avisos.length > 0 || jaTemApreciacao || poucasFotos) && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                  <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t('apreciacaoIa.confirmar.aviso.titulo')}
                  </div>
                  <ul className="list-inside list-disc space-y-0.5 text-xs text-amber-900">
                    {jaTemApreciacao && (
                      <li
                        dangerouslySetInnerHTML={{
                          __html: t('apreciacaoIa.confirmar.aviso.jaTemApreciacao'),
                        }}
                      />
                    )}
                    {poucasFotos && (
                      <li>
                        {t('apreciacaoIa.confirmar.aviso.poucasFotos', { total: preview.totalFotos })}
                      </li>
                    )}
                    {preview.avisos.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="mt-3 text-xs text-gray-500">{t('apreciacaoIa.confirmar.rodape')}</p>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            onClick={onFechar}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            {t('comum.acao.cancelar')}
          </button>
          <button
            onClick={iniciar}
            disabled={!preview || iniciando || semFotos}
            className="inline-flex items-center gap-1.5 rounded bg-purple-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            title={semFotos ? t('apreciacaoIa.confirmar.acao.semFotos') : undefined}
          >
            {iniciando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {t('apreciacaoIa.confirmar.acao.gerar')}
          </button>
        </div>
      </div>
    </div>
  );
}
